import React, { useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { UpdateTravelSetRequest } from 'generated';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Arrangement } from '../../types';

import {
  isUniqueArrangementCode,
  transformArrangementToEditRequest
} from './utils/utils';

import { mutations } from 'RestAPIs/rapini';

import { showSnackbar } from 'components/shared';
import { TableCRUDLayoutProps } from 'components/shared/CRUD/TableCRUDLayout';
import EditPage from 'components/shared/CRUD/components/InnerPage/EditPage';
import useForceRefresh from 'components/shared/hooks/useForceRefresh';

import { useGetTravelLines } from '../../hooks/TravelLines/useGetTravelLines';
import { editArrangementSchema } from '../../schema';
import { ArrangementFormFields } from './ArrangementFormFields';

interface Props {
  setIsInnerLayoutOpen: (isOpen: boolean) => void;
  toggleInnerLayout: () => void;
  refreshList: () => void;
  rowData: Arrangement;
  rows: TableCRUDLayoutProps<Arrangement>['rows'];
}

const EditArrangement: React.FC<Props> = ({
  rowData,
  setIsInnerLayoutOpen,
  toggleInnerLayout,
  refreshList,
  rows
}) => {
  const { t } = useTranslation('common');
  const { mutate: updateTravelSet } = mutations.useUpdateTravelSet();

  const [currentViewData, setCurrentViewData] = useState(rowData);
  const { data: travelLines } = useGetTravelLines(currentViewData?.trvSetId);
  const editData = useMemo(() => {
    if (currentViewData) {
      return {
        id: currentViewData.trvSetId,
        ...currentViewData
      };
    }

    return currentViewData;
  }, [currentViewData]);

  const { timestamp: isEditable, refresh: setIsEditable } = useForceRefresh();

  const methods = useForm<any>({
    defaultValues: editArrangementSchema.cast({
      ...editData
    }),
    resolver: yupResolver(editArrangementSchema),
    mode: 'onChange'
  });

  useEffect(() => {
    if (travelLines) {
      methods.reset({
        ...editArrangementSchema.cast({
          ...editData,
          travelSetLines: travelLines
        })
      });
    }
  }, [travelLines, currentViewData]);

  const onSubmit = async (data) => {
    const dataToSave = transformArrangementToEditRequest(
      data
    ) as unknown as UpdateTravelSetRequest;

    if (!isUniqueArrangementCode(data.code, rowData.code, rows)) {
      return methods.setError('code', {
        type: 'manual',
        message: t('validation.unique', {
          name: t('adminPortal.arrangements.code')
        })
      });
    }

    await updateTravelSet(dataToSave, {
      onSuccess: () => {
        showSnackbar({ message: t('changesSaved'), severity: 'success' });
        setIsEditable();
        methods.reset(data);
        refreshList();
      },
      onError: () => {
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    });
  };

  return (
    <EditPage
      onCancel={() => methods.reset()}
      methods={methods}
      onSubmit={methods.handleSubmit(onSubmit)}
      toggleInnerLayout={toggleInnerLayout}
      currentView={currentViewData}
      setCurrentView={setCurrentViewData}
      views={rows}
      isEditable={isEditable}
    >
      <ArrangementFormFields
        rowData={currentViewData}
        closeInnerView={() => setIsInnerLayoutOpen(false)}
      />
    </EditPage>
  );
};

export default EditArrangement;
